import FormFieldTsoPq from '@components/Form/FormFieldTsoPq';
import FormTsoPq from '@components/Form/FormTsoPq';
import { history } from '@constants/measurment';
import {
  useDeleteMeasurementDeviceModelByIdMutation,
  usePostMeasurementDeviceModelMutation,
  usePutMeasurementDeviceModelMutation,
} from '@redux/apiSlices/measurementDeviceModelApi';
import { useAppDispatch } from '@redux/hooks';
import { setSnackbar } from '@redux/slices/snackbarSlice';
import { commentSchema, mandatoryStringSchema, noiseThresholdSchema } from '@utils/validationSchema';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { object } from 'zod';
import FormModalWrapper from '../GenericModalWrapper';
import { StyledInputsWrapper } from '../StyledMasterdataModal';
import { RowDataMeasuringDevice } from './MeasuringDevice';

type MeasuringDeviceProps = {
  newMeasuringDevice?: boolean;
  handleDiscard?: any;
  defaultValues?: Record<string, any>;
  selectedDevice?: RowDataMeasuringDevice;
  updateDisabled?: boolean;
  deleteDisabled?: boolean;
};

export const FormContentCreator = ({
  props,
  updateDisabled,
  defaultValues,
}: {
  props: any;
  updateDisabled?: boolean;
  defaultValues?: Record<string, any>;
}) => {
  return <FormContent {...props} updateDisabled={updateDisabled} defaultValues={defaultValues} />;
};

const FormContent = ({
  register,
  errors,
  disabled,
  updateDisabled,
  defaultValues,
}: {
  register: any;
  errors: any;
  disabled: boolean;
  updateDisabled?: boolean;
  defaultValues?: Record<string, any>;
}) => {
  const { t } = useTranslation();
  const fields: Array<{
    name: string;
    label: string;
    required?: boolean;
    multiline?: boolean;
    rows?: number;
    customWidth?: string;
    type?: 'number' | 'floatingNumber' | 'text';
    supportingText?: string;
    disabled?: boolean;
  }> = [
    {
      name: 'manufacturer',
      label: t('masterdata.measuringDeviceType.table.manufacturer'),
      required: true,
    },
    {
      name: 'model',
      label: t('masterdata.measuringDeviceType.table.model'),
      required: true,
    },
    {
      name: 'noiseThresholdVolt',
      label: t('masterdata.measuringDeviceType.table.noiseLimitVoltage'),
      required: false,
      type: 'floatingNumber',
      supportingText:
        updateDisabled && defaultValues?.noiseThresholdVolt != null
          ? t('masterdata.generalLimitationHasData')
          : t('masterdata.measuringDeviceType.noiseLimit.supportingText'),
      disabled: updateDisabled && defaultValues?.noiseThresholdVolt != null,
    },
    {
      name: 'noiseThresholdAmpere',
      label: t('masterdata.measuringDeviceType.table.noiseLimitCurrent'),
      required: false,
      type: 'floatingNumber',
      supportingText:
        updateDisabled && defaultValues?.noiseThresholdAmpere != null
          ? t('masterdata.generalLimitationHasData')
          : t('masterdata.measuringDeviceType.noiseLimit.supportingText'),
      disabled: updateDisabled && defaultValues?.noiseThresholdAmpere != null,
    },
    {
      name: 'comment',
      label: t('management.substation.comment'),
      multiline: true,
      customWidth: '47.125rem',
    },
  ];

  return (
    <StyledInputsWrapper>
      {fields.map(
        ({
          name,
          label,
          required = false,
          multiline = false,
          rows = 1,
          customWidth,
          type = 'text',
          supportingText,
          disabled = false,
        }) => (
          <FormFieldTsoPq
            key={name}
            name={name}
            label={label}
            type={type}
            errorMessage="general.error.characterLimit"
            register={register}
            multiline={multiline}
            rows={rows}
            customWidth={customWidth ?? '19.25rem'}
            placeholder={'masterdata.generalPlaceholder'}
            required={required}
            errors={errors}
            supportingText={supportingText}
            disabled={disabled}
          />
        )
      )}
    </StyledInputsWrapper>
  );
};
const MeasuringDeviceModal = ({
  newMeasuringDevice,
  handleDiscard,
  defaultValues = {},
  selectedDevice,
  updateDisabled,
  deleteDisabled,
}: MeasuringDeviceProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [addMeasurementDeviceModel] = usePostMeasurementDeviceModelMutation();
  const [editMeasurementDeviceModel] = usePutMeasurementDeviceModelMutation();
  const [deleteMeasurementDeviceModelById] = useDeleteMeasurementDeviceModelByIdMutation();

  const validationSchema = object({
    manufacturer: mandatoryStringSchema,
    model: mandatoryStringSchema,
    noiseThresholdVolt: noiseThresholdSchema,
    noiseThresholdAmpere: noiseThresholdSchema,
    comment: commentSchema,
  });

  const renderFormContent = useCallback(
    (props: any) => <FormContent {...props} hasData={updateDisabled} defaultValues={defaultValues} />,
    [updateDisabled, defaultValues]
  );

  const handleDeleteConfirm = async () => {
    if (!selectedDevice?.id) return;
    try {
      await deleteMeasurementDeviceModelById(selectedDevice.id).unwrap();
      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: t('snackbar.measurementDeviceModel.delete.success'),
          copyToClipboard: false,
          requestID: undefined,
        })
      );
    } catch (err: any) {
      const requestID = err?.data?.traceId;
      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('snackbar.measurementDeviceModel.delete.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    } finally {
      handleDiscard();
    }
  };

  const handleSubmit = async (data: any) => {
    const payload = newMeasuringDevice ? { externalDto: data } : { externalDto: { ...selectedDevice, ...data } };

    try {
      if (newMeasuringDevice) {
        await addMeasurementDeviceModel(payload).unwrap();
        dispatch(
          setSnackbar({
            open: true,
            type: 'success',
            textContent: t('snackbar.measurementDeviceModel.new.success'),
            copyToClipboard: false,
            requestID: undefined,
          })
        );
      } else {
        await editMeasurementDeviceModel(payload).unwrap();
        dispatch(
          setSnackbar({
            open: true,
            type: 'success',
            textContent: t('snackbar.measurementDeviceModel.update.success'),
            copyToClipboard: false,
            requestID: undefined,
          })
        );
      }
    } catch (err: any) {
      const requestID = err?.data?.traceId;
      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: newMeasuringDevice
            ? t('snackbar.measurementDeviceModel.new.error')
            : t('snackbar.measurementDeviceModel.update.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    } finally {
      handleDiscard();
    }
  };

  return (
    <FormModalWrapper
      show={!!(selectedDevice || newMeasuringDevice)}
      newItem={newMeasuringDevice}
      title={t('masterdata.measuringDeviceType.modal.title')}
      titleEdit={t('masterdata.measuringDeviceType.modal.titleEdit')}
      onSubmit={handleSubmit}
      onDiscard={handleDiscard}
      defaultValues={defaultValues}
      showDeleteButton={!!selectedDevice}
      formContent={renderFormContent}
      formComponent={FormTsoPq}
      formProps={{ customProps: history, schema: validationSchema }}
      deleteButtonDisabled={deleteDisabled}
      tooltipText={
        deleteDisabled
          ? t('masterdata.measuringDeviceType.cannotDelete')
          : t('masterdata.measuringDeviceType.deleteTitle')
      }
      deleteTitle={t('masterdata.measuringDeviceType.deleteTitle')}
      deleteText={t('masterdata.measuringDeviceType.deleteContent')}
      onDeleteConfirm={handleDeleteConfirm}
    />
  );
};

export default MeasuringDeviceModal;
